import React, { FC } from 'react';
import { observer } from 'mobx-react-lite';
import useMiniCard from 'hooks/useMiniCard';
import { useStores } from 'hooks/useStores';
import cn from 'classnames';
import decline from 'utils/decline';
import dotsToCommas from 'utils/dotsToCommas';
import mileageIcon from 'src/icons/mileage.svg';
import truckBodyIcon from 'src/icons/truck-body.svg';
import truckTypeIcon from 'src/icons/truck-type.svg';
import weightIcon from 'icons/weight.svg';
import sizeIcon from 'icons/size.svg';
import engineVolumeIcon from 'icons/engine-volume.svg';
import enginePowerIcon from 'icons/engine-power.svg';
import gearboxIcon from 'icons/gearbox.svg';
import calendarIcon from 'icons/calendar.svg';
import AdvertItemDataModel from 'models/AdvertDataModel/AdvertItemDataModel';
import { ParametersStyles } from './ParametersStyles';
import Item from './components/Item/Item';

interface IProps {
  item: AdvertItemDataModel;
  itemType: string;
  className?: string;
}

const Parameters: FC<IProps> = observer(({ item, itemType, className }) => {
  const {
    dictionariesStore: dict,
    appStore: { screenClassNames },
  } = useStores();

  const {
    data: { advert, advertTypeClassNames },
  } = useMiniCard();

  const bodyTypeName = item.bodyType && dict.body_types[item.bodyType];
  const busTypeName = item.busType && dict.bus_types[item.busType];
  const excavatorTypeName = item.excavatorType && dict.excavator_types[item.excavatorType];
  const loaderTypeName = item.loaderType && dict.loader_types[item.loaderType];
  const tractorTypeName = item.tractorType && dict.tractor_types[item.tractorType];
  const engineTypeName = item.engineType && dict.engine_types[item.engineType];
  const gearboxTypeName = item.gearboxType && dict.gearbox_types[item.gearboxType];
  const from = advert.isPurchase ? 'от\u00A0' : '';
  const to = advert.isPurchase ? 'до\u00A0' : '';

  const getFirstRowThirdItem = () => {
    if (advert.isSpecialMachinery)
      return {
        data: item.formattedWorkingHours && `${to}${item.formattedWorkingHours}`,
        name: 'Моточасы',
      };
    return {
      data: item.formattedMileage && `${to}${item.formattedMileage}`,
      name: 'Пробег',
    };
  };

  const getSecondRowFirstItem = () => {
    if (advert.isBus) return { data: busTypeName, name: 'Тип автобуса' };
    if (advert.isExcavator) return { data: excavatorTypeName, name: 'Тип экскаватора' };
    if (advert.isLoader) return { data: loaderTypeName, name: 'Тип погрузчика' };
    if (item.isTractor) return { data: tractorTypeName, name: 'Тип тягача' };
    return { data: bodyTypeName, name: 'Тип кузова' };
  };

  const getSecondRowSecondItem = () => {
    if (advert.isBus) {
      const seatsCountData = item.seatsCount && (
        <span>
          {from}
          <b>{item.seatsCount}</b>&nbsp;
          {decline(
            item.seatsCount,
            advert.isPurchase ? ['места', 'мест', 'мест'] : ['место', 'места', 'мест'],
          )}
        </span>
      );
      return { data: seatsCountData, name: 'Количество мест' };
    }
    const capacityData = item.capacity && (
      <span>
        {from}
        <b>{dotsToCommas(item.capacity)}</b>&nbsp;т
      </span>
    );
    return { data: capacityData, name: 'Грузоподъемность' };
  };

  const getSecondRowThirdItem = () => {
    const volumeData = item.volume && (
      <span>
        {from}
        <b>{dotsToCommas(item.volume)}</b>&nbsp;м³
      </span>
    );
    return { data: volumeData, name: 'Объем кузова' };
  };

  const getThirdRowFirstItem = () => {
    const engineData = [item.engineVolume && `${item.engineVolume}\u00A0л`, engineTypeName]
      .filter(Boolean)
      .join(',\u00A0');
    return { data: engineData, name: 'Двигатель' };
  };

  const firstRowFirstItem = { data: item.nameOfType, name: 'Тип техники' };
  const firstRowSecondItem = {
    data: item.year && `${from}${item.year}\u00A0г.`,
    name: 'Год выпуска',
  };
  const firstRowThirdItem = getFirstRowThirdItem();

  const secondRowFirstItem = getSecondRowFirstItem();
  const secondRowSecondItem = getSecondRowSecondItem();
  const secondRowThirdItem = getSecondRowThirdItem();

  const thirdRowFirstItem = getThirdRowFirstItem();
  const thirdRowSecondItem = {
    data: item.enginePower && `${item.enginePower}\u00A0л.с.`,
    name: 'Мощность двигателя',
  };
  const thirdRowThirdItem = {
    data: gearboxTypeName,
    name: 'Коробка передач',
  };

  return (
    <ParametersStyles className={cn(className, screenClassNames, advertTypeClassNames)}>
      <table>
        <tbody>
          <tr className="first-row">
            <td className="first-item">
              <Item data={firstRowFirstItem} icon={truckTypeIcon} />
            </td>
            <td className="second-item">
              <Item data={firstRowSecondItem} icon={calendarIcon} />
            </td>
            <td className="third-item">
              <Item data={firstRowThirdItem} icon={mileageIcon} />
            </td>
          </tr>
          <tr className="second-row">
            <td className="first-item">
              <Item data={secondRowFirstItem} icon={truckBodyIcon} />
            </td>
            <td className="second-item">
              <Item data={secondRowSecondItem} icon={weightIcon} />
            </td>
            <td className="third-item">
              <Item data={secondRowThirdItem} icon={sizeIcon} />
            </td>
          </tr>
          <tr className="third-row">
            <td className="first-item">
              <Item data={thirdRowFirstItem} icon={engineVolumeIcon} />
            </td>
            <td className="second-item">
              <Item data={thirdRowSecondItem} icon={enginePowerIcon} />
            </td>
            <td className="third-item">
              <Item data={thirdRowThirdItem} icon={gearboxIcon} />
            </td>
          </tr>
        </tbody>
      </table>
    </ParametersStyles>
  );
});

export default Parameters;
